import type { ScamType } from './types'

/**
 * Canonical scam type registry. Each entry powers a per-state canonical
 * page at /states/<slug>/scams/<scamSlug>. State packs reference these
 * by id via `topScams[].id`.
 */
const SCAM_TYPES: ScamType[] = [
  {
    slug: 'investment-fraud',
    name: 'Investment Fraud',
    tagline: 'Fake investment apps, "pig butchering" crypto schemes, and impostor brokers.',
    matchIds: ['investment-fraud', 'investment'],
    courseChapter: 'warning-signs',
    what: 'Scammers convince a target to invest, usually in cryptocurrency, through a fake trading platform. The target sees fake gains, deposits more, and only realizes the platform is fraudulent when they try to withdraw.',
    playbook: [
      {
        step: 'The wrong-number opener',
        detail: 'A stranger texts something casual ("hey, dinner Friday?") to start a conversation that has nothing to do with money.',
      },
      {
        step: 'The trust build',
        detail: 'Weeks of friendly chat establish rapport. The scammer mentions a successful investment in passing.',
      },
      {
        step: 'The "exclusive" platform',
        detail: 'The target is invited to invest through a custom-built fake exchange that shows fabricated returns.',
      },
      {
        step: 'The wall',
        detail: 'Withdrawal requests trigger fake "tax" or "verification" demands. Money never comes back.',
      },
    ],
    redFlags: [
      'A stranger DMs you and the conversation drifts toward investing.',
      'You\'re told about a platform you\'ve never heard of with unusually high returns.',
      'You can deposit easily but withdrawals require a fee or a tax payment first.',
      'You\'re asked to keep the opportunity confidential.',
    ],
    ifTargeted: [
      'Stop sending money the moment a withdrawal is blocked. Any balance the platform shows is almost certainly gone.',
      'Take screenshots of the platform, all messages, and any wallet addresses involved.',
      'Report to the FBI IC3 immediately. The faster you report, the better the chance of any recovery.',
      'Contact your bank or crypto exchange to flag the destination addresses.',
    ],
  },
  {
    slug: 'business-email-compromise',
    name: 'Business Email Compromise',
    tagline: 'Wire-transfer redirection through impersonated executives, vendors, or title companies.',
    matchIds: ['business-email-compromise', 'business-email', 'bec'],
    courseChapter: 'pause-and-verify',
    what: 'Scammers impersonate someone the target trusts (a CEO, vendor, attorney, or escrow agent) to redirect a legitimate wire transfer to a fraudulent account, often during a real-estate closing or vendor payment.',
    playbook: [
      {
        step: 'The recon',
        detail: 'Scammers research the target\'s vendors, ongoing transactions, and email patterns from public sources or compromised inboxes.',
      },
      {
        step: 'The look-alike',
        detail: 'They register a domain that\'s one character off from a real one, or compromise a legitimate email account directly.',
      },
      {
        step: 'The handoff',
        detail: 'At the moment a real wire is expected, they send updated banking instructions that look identical to legitimate paperwork.',
      },
      {
        step: 'The transfer',
        detail: 'Once the wire goes through, the funds are moved through a chain of accounts within hours.',
      },
    ],
    redFlags: [
      'Banking instructions arrive late in a transaction with a request to update wire details.',
      'The sender\'s domain has a subtle difference from prior emails.',
      'The new account is at a different bank than originally specified.',
      'There\'s pressure to move quickly to avoid "delaying the closing".',
    ],
    ifTargeted: [
      'Call the original sender at a number you already had. Do not use any contact info from the suspect email.',
      'If a wire has already been sent, contact your bank within hours to request a recall.',
      'Report to FBI IC3 immediately. Recovery rates drop sharply after 72 hours.',
    ],
  },
  {
    slug: 'tech-support',
    name: 'Tech Support Scams',
    tagline: 'Fake Microsoft, Apple, and bank fraud-department calls that drain accounts via remote access.',
    matchIds: ['tech-support', 'technical-support', 'tech'],
    courseChapter: 'warning-signs',
    what: 'A pop-up, call, or email claims the target\'s computer is infected or their bank account is compromised. The "fix" involves giving remote access or moving money to a "safe" account, which is the scammer\'s.',
    playbook: [
      {
        step: 'The alarm',
        detail: 'A locked-screen popup or phone call announces a serious computer or banking emergency.',
      },
      {
        step: 'The hand-off',
        detail: 'The target is told to call a number or click a link that connects to the "tech support team".',
      },
      {
        step: 'Remote access',
        detail: 'The target installs software (AnyDesk, TeamViewer) that gives the scammer full control of their computer.',
      },
      {
        step: 'The drain',
        detail: 'The scammer "shows" fake fraud activity, then directs the target to wire savings to a "secure" account.',
      },
    ],
    redFlags: [
      'A popup or call you didn\'t initiate claims your computer or account is compromised.',
      'You\'re told to install remote access software like AnyDesk or TeamViewer.',
      'You\'re instructed to move money to a different account "for safety".',
      'You\'re told not to discuss the situation with bank tellers or family.',
    ],
    ifTargeted: [
      'Hang up. Real tech support and real bank fraud departments do not call out of the blue and ask for remote access.',
      'If you gave remote access, disconnect the computer from the internet, then take it to a trusted technician.',
      'Call your bank directly using the number on the back of your card. Freeze accounts if money has moved.',
      'Report to FBI IC3 and the FTC.',
    ],
  },
  {
    slug: 'romance-scams',
    name: 'Romance Scams',
    tagline: 'Months-long online relationships engineered to set up an emergency money request.',
    matchIds: ['romance-scams', 'romance', 'confidence'],
    courseChapter: 'emotional-hijack',
    what: 'A scammer builds a months-long online relationship through a dating app or social media, then engineers an emergency that requires the target to send money. Often a feeder for crypto investment fraud.',
    playbook: [
      {
        step: 'The match',
        detail: 'Contact begins on a dating app or unsolicited DM. The scammer\'s profile is curated to match the target\'s preferences.',
      },
      {
        step: 'The bond',
        detail: 'Daily messages, voice notes, and shared "dreams" build deep emotional connection over weeks or months.',
      },
      {
        step: 'The crisis',
        detail: 'A medical emergency, customs hold, business problem, or family crisis appears. The scammer needs help fast.',
      },
      {
        step: 'The escalation',
        detail: 'After the first transfer, the requests grow. If suspicion appears, a "rescue" plot extends the manipulation.',
      },
    ],
    redFlags: [
      'They never video-call, or only for a moment with a poor connection.',
      'They escalate quickly to professions of love.',
      'They have an emergency that only money can solve.',
      'They ask you to send money via crypto, gift cards, or wire transfer.',
    ],
    ifTargeted: [
      'Stop sending money. Any request for funds is the scam, regardless of the story.',
      'Reverse-image search their photos. Most are stolen from real public profiles.',
      'Talk to a family member or friend. The emotional grip loosens when the scenario is said out loud.',
      'Report to FBI IC3 and the dating platform.',
    ],
  },
  {
    slug: 'government-impersonation',
    name: 'Government Impersonation',
    tagline: 'Fake IRS, Social Security, and ICE agents demanding payment via gift cards or wire transfer.',
    matchIds: ['government-impersonation', 'govt-impersonation', 'government'],
    courseChapter: 'warning-signs',
    what: 'Callers claim to be from the IRS, Social Security Administration, or law enforcement and demand immediate payment to avoid arrest, deportation, or benefit suspension.',
    playbook: [
      {
        step: 'The threat call',
        detail: 'A caller claiming to be from a government agency says there\'s a serious problem requiring immediate action.',
      },
      {
        step: 'The official tone',
        detail: 'They cite case numbers, badge numbers, and may "transfer" the call to another official to add weight.',
      },
      {
        step: 'The unusual payment',
        detail: 'They demand payment via gift cards, cryptocurrency, or wire transfer (never how a real agency works).',
      },
      {
        step: 'The pressure',
        detail: 'They keep the target on the phone, sometimes for hours, to prevent them from verifying or talking to family.',
      },
    ],
    redFlags: [
      'A government agency calls you and demands immediate payment.',
      'You\'re told to pay with gift cards, cryptocurrency, or wire transfer.',
      'You\'re threatened with immediate arrest or deportation.',
      'You\'re told not to hang up or talk to anyone.',
    ],
    ifTargeted: [
      'Hang up. Government agencies do not threaten arrest by phone or demand payment in gift cards.',
      'Call the agency back at a number you find independently (not one the caller gave you).',
      'Report to the FTC and FBI IC3.',
    ],
  },
]

export function listScamTypes(): ScamType[] {
  return SCAM_TYPES
}

export function getScamTypeBySlug(slug: string): ScamType | null {
  return SCAM_TYPES.find(s => s.slug === slug) ?? null
}

/**
 * Resolve a state pack `topScams[].id` to a canonical scam type.
 * Returns null if no canonical match exists (fall back to plain text).
 */
export function resolveScamType(id: string | undefined | null): ScamType | null {
  if (!id) return null
  const norm = id.toLowerCase()
  return (
    SCAM_TYPES.find(s => s.matchIds.includes(norm) || s.slug === norm) ?? null
  )
}

export type { ScamType } from './types'
